import { observable, runInAction } from 'mobx'

/*
 * Controls WHEN the Opus AI assistant is mounted, so its bundle stops being part
 * of the first paint.
 *
 * `@mylong.io/ai-chat-web` is 316KB and `assistant.client.js` imports it for its
 * side effect (it registers the <opus-assistant> element and window.OpusAssistant).
 * Because `app.js` imported the component statically, that 316KB sat in the main
 * chunk and the widget's own `/connections` and `/latest?persona=recruiter` calls
 * went out during boot — competing with the page's own queries — on every screen,
 * before anyone had asked for the assistant.
 *
 * The widget now loads when someone actually asks for it — the same pattern every
 * third-party chat widget uses — with a prefetch on hover so the first press still
 * feels immediate. Nothing about the widget's behaviour once mounted changes.
 */
const state = observable({ mounted: false })
let prefetched = null
let initialised = false
let pendingOpen = false

export const assistantGate = {
	get mounted() {
		return state.mounted
	},

	mount() {
		if (!state.mounted) {
			runInAction(() => {
				state.mounted = true
			})
		}
	},

	/*
	 * Warm the chunk without mounting anything — used on hover/focus of the "Ask AI"
	 * button, which is what makes the first press feel instant despite the bundle
	 * no longer being preloaded. Safe to call repeatedly; webpack dedupes.
	 */
	prefetch() {
		if (!prefetched) {
			prefetched = import('./assistant.client')
		}
		return prefetched
	},

	/*
	 * Called by assistant.client once `window.OpusAssistant.init(...)` has run.
	 *
	 * `window.OpusAssistant` existing is NOT the same thing: the widget bundle
	 * registers it as a side effect of being imported, which now happens on hover
	 * via prefetch — before <Assistant/> has rendered, let alone configured it.
	 * Toggling in that window is silently lost, which is exactly what a press
	 * straight after a hover used to do.
	 */
	markInitialised() {
		initialised = true
		if (pendingOpen) {
			pendingOpen = false
			window.OpusAssistant.toggle()
		}
	},

	/*
	 * What the "Ask AI" button calls. Mounts the widget if it is not up yet and
	 * opens it once it is genuinely ready — immediately when it already is.
	 */
	open() {
		this.prefetch()
		this.mount()

		if (initialised && window.OpusAssistant) {
			window.OpusAssistant.toggle()
			return
		}

		pendingOpen = true
	},
}
